import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Bot, User, Loader2, Sparkles, RotateCcw, Settings } from 'lucide-react';
import { api, companyAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const SUGGESTION_KEYS = ["s1", "s2", "s3", "s4", "s5"];

const inlineFormat = (text) =>
  text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:rgba(0,0,0,0.2);padding:0 3px;border-radius:3px;font-size:0.8em">$1</code>');

const renderMarkdown = (text) => {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // H2/H3 headings
    if (/^###\s/.test(line)) {
      elements.push(<p key={i} className="font-semibold mt-2 mb-0.5" dangerouslySetInnerHTML={{ __html: inlineFormat(line.replace(/^###\s/, '')) }} />);
      i++; continue;
    }
    if (/^##\s/.test(line)) {
      elements.push(<p key={i} className="font-bold mt-2 mb-0.5 text-base" dangerouslySetInnerHTML={{ __html: inlineFormat(line.replace(/^##\s/, '')) }} />);
      i++; continue;
    }
    if (/^#\s/.test(line)) {
      elements.push(<p key={i} className="font-bold mt-1 mb-1 text-base" dangerouslySetInnerHTML={{ __html: inlineFormat(line.replace(/^#\s/, '')) }} />);
      i++; continue;
    }
    // Bullet list items
    if (/^[-•]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-•]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-•]\s/, ''));
        i++;
      }
      elements.push(
        <ul key={i} style={{ paddingLeft: '1.2em', margin: '2px 0', listStyleType: 'disc' }}>
          {items.map((item, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />)}
        </ul>
      );
      continue;
    }
    // Empty line → small gap
    if (line.trim() === '') {
      elements.push(<div key={i} style={{ height: '6px' }} />);
    } else {
      elements.push(<p key={i} style={{ margin: '1px 0' }} dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />);
    }
    i++;
  }
  return elements;
};

const MessageBubble = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
        style={{ background: isUser ? '#5e6ad2' : 'linear-gradient(135deg, #5e6ad2, #828fff)' }}
      >
        {isUser
          ? <User className="h-4 w-4 text-white" />
          : <Bot className="h-4 w-4 text-white" />
        }
      </div>
      <div
        className="max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed"
        style={isUser
          ? { backgroundColor: '#5e6ad2', color: '#fff', borderRadius: '18px 4px 18px 18px' }
          : { backgroundColor: 'var(--lm-s2)', border: '1px solid var(--lm-border)', color: 'var(--lm-ink)', borderRadius: '4px 18px 18px 18px' }
        }
      >
        {isUser ? msg.content : renderMarkdown(msg.content)}
      </div>
    </div>
  );
};

const TypingIndicator = () => {
  const { t } = useTranslation();
  return (
  <div className="flex gap-3">
    <div
      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #5e6ad2, #828fff)' }}
    >
      <Bot className="h-4 w-4 text-white" />
    </div>
    <div
      className="rounded-2xl px-4 py-3"
      style={{ backgroundColor: 'var(--lm-s2)', border: '1px solid var(--lm-border)', borderRadius: '4px 18px 18px 18px' }}
    >
      <div className="flex items-center gap-1.5">
        <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: '#5e6ad2' }} />
        <span className="text-xs" style={{ color: 'var(--lm-ink-3)' }}>{t("hrAnalyst.typing")}</span>
      </div>
    </div>
  </div>
  );
};

const HRAnalyst = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [aiConfigured, setAiConfigured] = useState(null); // null=loading, true/false
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: t("hrAnalyst.greeting"),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    companyAPI.getAISettings()
      .then(r => setAiConfigured(r.data.key_configured && r.data.enabled))
      .catch(() => setAiConfigured(true)); // if endpoint fails, show the chat (graceful)
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  if (aiConfigured === null) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  if (aiConfigured === false) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
        <div className="p-4 rounded-full bg-muted">
          <Bot className="h-10 w-10 text-muted-foreground" />
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">{t("hrAnalyst.notConfiguredTitle")}</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t("hrAnalyst.notConfiguredDesc")}
          </p>
        </div>
        <button
          onClick={() => navigate('/company-configuration')}
          className="flex items-center gap-2 px-4 py-2 rounded-md border text-sm hover:bg-muted transition-colors"
        >
          <Settings className="h-4 w-4" />
          {t("hrAnalyst.configureAi")}
        </button>
      </div>
    );
  }

  const sendMessage = async (text) => {
    const userMessage = text || input.trim();
    if (!userMessage || loading) return;

    setInput('');
    const history = messages.slice(1);
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const res = await api.post('v1/agents/hr-analyst/chat', {
        message: userMessage,
        history: history.map(m => ({ role: m.role, content: m.content })),
      });

      if (res.data?.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      } else {
        throw new Error('Invalid response');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const notConfigured = detail?.configured === false || detail?.includes?.('non configurée');
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: notConfigured
            ? t("hrAnalyst.aiNotConfiguredMsg")
            : t("hrAnalyst.errorMsg"),
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const reset = () => {
    setMessages([{
      role: 'assistant',
      content: t("hrAnalyst.resetMsg"),
    }]);
    setInput('');
    inputRef.current?.focus();
  };

  const showSuggestions = messages.length === 1;

  return (
    <div className="flex flex-col max-w-3xl mx-auto" style={{ height: 'calc(100vh - 8rem)' }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="p-2.5 rounded-xl"
            style={{ background: 'linear-gradient(135deg, #5e6ad2, #828fff)', boxShadow: '0 4px 12px rgba(94,106,210,0.35)' }}
          >
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--lm-ink)' }}>{t("hrAnalyst.title")}</h1>
            <p className="text-xs" style={{ color: 'var(--lm-ink-3)' }}>{t("hrAnalyst.subtitle")}</p>
          </div>
        </div>
        <button
          onClick={reset}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors"
          style={{ color: 'var(--lm-ink-3)', border: '1px solid var(--lm-border)' }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--lm-ink)'; e.currentTarget.style.borderColor = 'var(--lm-ink-2)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--lm-ink-3)'; e.currentTarget.style.borderColor = 'var(--lm-border)'; }}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t("hrAnalyst.reset")}
        </button>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1 pb-2">
        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} />
        ))}
        {loading && <TypingIndicator />}
        <div ref={bottomRef} />
      </div>

      {/* Suggestions */}
      {showSuggestions && (
        <div className="py-3">
          <p className="text-xs mb-2" style={{ color: 'var(--lm-ink-3)' }}>{t("hrAnalyst.suggestionsLabel")}</p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTION_KEYS.map((key, i) => (
              <button
                key={i}
                onClick={() => sendMessage(t(`hrAnalyst.${key}`))}
                className="text-xs px-3 py-1.5 rounded-full transition-all"
                style={{ backgroundColor: 'var(--lm-s2)', border: '1px solid var(--lm-border)', color: 'var(--lm-ink-2)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#5e6ad2'; e.currentTarget.style.color = '#828fff'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--lm-border)'; e.currentTarget.style.color = 'var(--lm-ink-2)'; }}
              >
                {t(`hrAnalyst.${key}`)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="pt-3" style={{ borderTop: '1px solid var(--lm-border)' }}>
        <div
          className="flex gap-2 rounded-2xl px-4 py-2 transition-all"
          style={{ backgroundColor: 'var(--lm-s2)', border: '1px solid var(--lm-border)' }}
          onFocusCapture={e => e.currentTarget.style.borderColor = '#5e6ad2'}
          onBlurCapture={e => e.currentTarget.style.borderColor = 'var(--lm-border)'}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("hrAnalyst.inputPlaceholder")}
            rows={1}
            className="flex-1 resize-none text-sm outline-none bg-transparent max-h-28"
            style={{ color: 'var(--lm-ink)', lineHeight: '1.5rem' }}
            disabled={loading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="self-end p-1.5 rounded-xl text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#5e6ad2' }}
            onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#828fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#5e6ad2'; }}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-center mt-1.5" style={{ color: 'var(--lm-ink-3)' }}>
          {t("hrAnalyst.footer")}
        </p>
      </div>
    </div>
  );
};

export default HRAnalyst;
